import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, Row, Col, Switch, message } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import Heading from "../../components/Heading";
import { capitaliseWithHyphen } from "../../utils";
import "./style.scss";
import urls from "../../api/urls";
import API from "../../api";

const { Option } = Select;

const DBOnboarding = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchingPincode, setFetchingPincode] = useState(false);
  const { DP_ON_BOARDING, DP_DETAILS, DP_UPDATE_DETAILS } = urls;
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're in edit mode and get the data
  const isEditMode = location.state?.editMode || false;
  const deliveryPartnerData = location.state?.deliveryPartnerData || null;

  // Fetch delivery partner details for editing
  useEffect(() => {
    if (isEditMode && deliveryPartnerData?.id) {
      fetchDeliveryPartnerDetails();
    }
  }, [isEditMode, deliveryPartnerData]);

  // Function to fetch delivery partner details
  const fetchDeliveryPartnerDetails = async () => {
    setLoading(true);
    try {
      console.log("Fetching details for DP ID:", deliveryPartnerData.id);

      // Make API call to get full delivery partner details
      const url = `${DP_DETAILS}/${deliveryPartnerData.id}`;
      let apiResult = await API.Api(
        url
      ).getWithAllHeadersDP();

      console.log("DP Details API Response:", apiResult);

      if (apiResult.status) {
        const result = apiResult.response.data;
        console.log("Parsed API data:", result);

        if (result.statusCode === 200 && result.data && result.data.length > 0) {
          const dpData = result.data[0]; // Get first item from data array

          // Populate form with all available data
          form.setFieldsValue({
            dpName: dpData.dpName || "",
            officialEmail: dpData.officialEmail || "",
            officialContactNumber: dpData.officialContact || "",
            personalEmail: dpData.personalEmail || "",
            personalContactNumber: dpData.personalContact || "",
            gstNumber: dpData.gstNumber || "",
            addressLine1: dpData.addressLine1 || "",
            city: dpData.city || "",
            state: dpData.state || "",
            pincode: dpData.pincode ? dpData.pincode.toString() : "",
            zone: dpData.zone || "",
            latitude: dpData.latitude || "",
            longitude: dpData.longitude || "",
            isActive: dpData.isActive !== undefined ? dpData.isActive : true
          });

          console.log("Form populated with DP details:", dpData);
        } else {
          message.error(result.message || "Failed to fetch delivery partner details");
          // Fallback to basic data
          form.setFieldsValue({
            dpName: deliveryPartnerData.dpName,
          });
        }
      } else {
        throw new Error(apiResult.response?.statusText || "API call failed");
      }
    } catch (error) {
      console.error("Error fetching delivery partner details:", error);
      message.error(`Failed to load delivery partner details: ${error.message}`);

      // Fallback to basic data we have
      form.setFieldsValue({
        dpName: deliveryPartnerData.dpName,
      });
    } finally {
      setLoading(false);
    }
  };

  const zones = [
    { value: "North", label: "North" },
    { value: "South", label: "South" },
    { value: "East", label: "East" },
    { value: "West", label: "West" },
    { value: "Central", label: "Central" },
    { value: "Northeast", label: "Northeast" }
  ];

  const states = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
    "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
    "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
    "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
    "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
  ];

  // Function to handle mobile number input - only allow digits and limit to 10 characters
  const handleMobileNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Remove all non-digit characters
    if (value.length <= 10) {
      return value;
    }
    return value.slice(0, 10); // Limit to 10 digits
  };

  // Function to fetch pincode details - following project pattern
  const handlePincodeInput = async (e) => {
    const pincode = e.target.value;
    if (pincode.length > 5) {
      try {
        setFetchingPincode(true);
        let response = await fetch(
          `https://api.postalpincode.in/pincode/${pincode}`
        );
        let res = await response.json();
        let data = res[0]?.PostOffice[0];

        if (res[0]?.Status === "Success") {
          // Set city and state in form using project's capitalization utility
          form.setFieldsValue({
            city: capitaliseWithHyphen(data?.District),
            state: capitaliseWithHyphen(data?.State)
          });

          //message.success(`City and state updated automatically for pincode ${pincode}`);
        } else {
          message.error("Invalid pincode or no data found");
        }
      } catch (error) {
        console.error("Error fetching pincode details:", error);
        message.error("Failed to fetch pincode details. Please enter city and state manually.");
      } finally {
        setFetchingPincode(false);
      }
    } else {
      // Clear city and state if pincode is less than 6 digits
      form.setFieldsValue({
        city: null,
        state: null
      });
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Prepare request body according to API specification
      const requestBody = {
        dpName: values.dpName,
        officialEmail: values.officialEmail,
        officialContact: values.officialContactNumber,
        personalEmail: values.personalEmail,
        personalContact: values.personalContactNumber,
        gstNumber: values.gstNumber,
        addressLine1: values.addressLine1,
        city: values.city,
        state: values.state,
        pincode: parseInt(values.pincode),
        zone: values.zone,
        latitude: values.latitude ? values.latitude.toString() : null,
        longitude: values.longitude ? values.longitude.toString() : null
      };

      // Add isActive only in edit mode
      if (isEditMode) {
        requestBody.isActive = values.isActive !== undefined ? values.isActive : false;
      }

      console.log("API Request Body:", requestBody);

      // Validate required fields
      const requiredFields = ["dpName", "officialEmail", "officialContact", "personalEmail", "personalContact", "addressLine1", "city", "state", "pincode", "gstNumber", "zone"];
      const missingFields = requiredFields.filter(field => !requestBody[field]);

      if (missingFields.length > 0) {
        message.error(`Please fill in all required fields: ${missingFields.join(", ")}`);
        setLoading(false);
        return;
      }
      console.log("API Response: Edit", isEditMode);
      // Make API call using project's API wrapper
      let apiResponse;
      if (isEditMode && deliveryPartnerData?.id) {
        console.log("API Response: Edit Yes", isEditMode);
        console.log("Update Request Body:", requestBody);
        // Update existing delivery partner
        apiResponse = await API.Api(`${DP_UPDATE_DETAILS}/${deliveryPartnerData.id}`).putWithHeaderDP(requestBody);
      } else {
        console.log("API Response: Create New");
        // Create new delivery partner
        apiResponse = await API.Api(DP_ON_BOARDING).postWithHeaderDP(requestBody);
      }

      console.log("Full API Response:", apiResponse);
      console.log("API Response Status:", apiResponse.status);
      console.log("API Response Data:", apiResponse.response);

      if (apiResponse.status) {
        const result = apiResponse.response.data;
        const successMessage = isEditMode
          ? "Delivery Partner updated successfully!"
          : "Delivery Partner onboarding completed successfully!";
        message.success(successMessage);
        console.log("API Response:", result);

        if (isEditMode) {
          // Navigate back to list after successful update
          navigate("/delivery-partner-list");
        } else {
          // Reset form after successful creation
          form.resetFields();
        }
      } else {
        const errorMessage = apiResponse.response?.data?.message ||
          apiResponse.response?.statusText ||
          `Failed to ${isEditMode ? "update" : "complete onboarding"}`;
        throw new Error(errorMessage);
      }

    } catch (error) {
      console.error("Error submitting form:", error);
      message.error(`Failed to complete DB onboarding: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    navigate(-1); // Navigate to previous page
  };

  return (
    <div className="master-main-container profile-container">
      <Heading title={isEditMode ? "Edit Delivery Partner" : "Delivery Partner Onboarding"} />
      <Form
        autoComplete="off"
        className="global-form profile-container-form"
        layout="vertical"
        form={form}
        scrollToFirstError={{
          behavior: "smooth",
          block: "center",
          inline: "center",
        }}
        onFinish={handleSubmit}
        initialValues={{
          isActive: true
        }}
      >
        <Row className="global-form-row">
          <Col xs={1} sm={1} md={4} lg={4} xl={4}></Col>
          <Col xs={22} sm={22} md={16} lg={16} xl={16}>
            <Row gutter={20} className="mt-1 profile-details-container mainForm">
              <Col span={24} className="mb-1">
                <span className="master-title">Basic Details</span>
                <hr className="divider" />
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="DP Name"
                  name="dpName"
                  rules={[
                    { required: true, message: "Please enter DP name" },
                    { min: 2, message: "DP name must be at least 2 characters" }
                  ]}
                >
                  <Input placeholder="Enter DP name" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="GST Number"
                  name="gstNumber"
                  rules={[
                    { required: true, message: "Please enter GST number" },
                    {
                      pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                      message: "Please enter a valid GST number"
                    }
                  ]}
                >
                  <Input placeholder="Enter GST number" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="Zone"
                  name="zone"
                  rules={[{ required: true, message: "Please select zone" }]}
                >
                  <Select placeholder="Select zone">
                    {zones.map(zone => (
                      <Option key={zone.value} value={zone.value}>
                        {zone.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="Official Email"
                  name="officialEmail"
                  rules={[
                    { required: true, message: "Please enter official email" },
                    { type: "email", message: "Please enter a valid email" }
                  ]}
                >
                  <Input placeholder="Enter official email" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="Official Contact Number"
                  name="officialContactNumber"
                  rules={[
                    { required: true, message: "Please enter official contact number" },
                    {
                      pattern: /^[6-9]\d{9}$/,
                      message: "Please enter a valid 10-digit mobile number "
                    },
                   
                  ]}
                >
                  <Input 
                    placeholder="Enter 10-digit mobile number" 
                    maxLength={10}
                    onChange={(e) => {
                      const value = handleMobileNumberChange(e);
                      form.setFieldsValue({ officialContactNumber: value });
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="Personal Email"
                  name="personalEmail"
                  rules={[
                    { required: true, message: "Please enter personal email" },
                    { type: "email", message: "Please enter a valid email" }
                  ]}
                >
                  <Input placeholder="Enter personal email" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="Personal Contact Number"
                  name="personalContactNumber"
                  rules={[
                    { required: true, message: "Please enter personal contact number" },
                    {
                      pattern: /^[6-9]\d{9}$/,
                      message: "Please enter a valid 10-digit mobile number "
                    },
                    
                  ]}
                >
                  <Input 
                    placeholder="Enter 10-digit mobile number" 
                    maxLength={10}
                    onChange={(e) => {
                      const value = handleMobileNumberChange(e);
                      form.setFieldsValue({ personalContactNumber: value });
                    }}
                  />
                </Form.Item>
              </Col>

              <Col span={24} className="mb-1 mt-2">
                <span className="master-title">Address Details</span>
                <hr className="divider" />
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="Address Line 1"
                  name="addressLine1"
                  rules={[{ required: true, message: "Please enter address" }]}
                >
                  <Input placeholder="Enter address line 1" />
                </Form.Item>
              </Col>


              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="Pincode"
                  name="pincode"
                  rules={[
                    { required: true, message: "Please enter pincode" },
                    {
                      pattern: /^[1-9][0-9]{5}$/,
                      message: "Please enter a valid 6-digit pincode"
                    }
                  ]}
                  help={fetchingPincode ? "Fetching city and state details..." : "Enter 6-digit pincode to auto-fill city and state"}
                >
                  <Input
                    placeholder="Enter pincode"
                    onChange={handlePincodeInput}
                    suffix={fetchingPincode ? <span>🔄</span> : null}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="City"
                  name="city"
                  rules={[{ required: true, message: "Please enter city" }]}
                >
                  <Input
                    placeholder="Enter city (auto-filled from pincode)"
                    loading={fetchingPincode}
                    disabled={fetchingPincode}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="State"
                  name="state"
                  rules={[{ required: true, message: "Please select state" }]}
                >
                  <Select
                    placeholder="Select state (auto-filled from pincode)"
                    showSearch
                    loading={fetchingPincode}
                    disabled={fetchingPincode}
                  >
                    {states.map(state => (
                      <Option key={state} value={state}>
                        {state}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="Latitude"
                  name="latitude"
                  rules={[
                    {
                      pattern: /^-?([1-8]?[1-9]|[1-9]0)\.{1}\d{1,6}$/,
                      message: "Please enter a valid latitude (e.g., 28.123456)"
                    }
                  ]}
                >
                  <Input
                    placeholder="Enter latitude (e.g., 28.123456)"
                    type="number"
                    step="any"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label="Longitude"
                  name="longitude"
                  rules={[
                    {
                      pattern: /^-?([1-9]?[1-9]|1[0-7][0-9]|180)\.{1}\d{1,6}$/,
                      message: "Please enter a valid longitude (e.g., 77.123456)"
                    }
                  ]}
                >
                  <Input
                    placeholder="Enter longitude (e.g., 77.123456)"
                    type="number"
                    step="any"
                  />
                </Form.Item>
              </Col>



              {isEditMode && (
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <Form.Item
                    label="Active Status"
                    name="isActive"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                    />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                <div 
                  className="d-flex flex-end mb-3" 
                  style={{ marginTop: isEditMode ? "2rem" : "3rem" }}
                >
                  <Button
                    className="btn-style cancel-btn ml-1 w-10"
                    onClick={handleReset}
                  >
                    CANCEL
                  </Button>
                  <Button
                    className="save-btn ml-1"
                    htmlType="submit"
                    loading={loading}
                  >
                    {isEditMode ? "UPDATE" : "SAVE"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Col>
          <Col xs={1} sm={1} md={4} lg={4} xl={4}></Col>
        </Row>
      </Form>
    </div>
  );
};

export default DBOnboarding; 
